import { useEffect, useState } from 'react';
import { D, fmt, fmtPrice, fmtUsd, maxNotional, qtyToLots, type Side } from '@platform/shared';
import { cx, fmtLots, Modal, sideLabel, Spinner, Tabs, unitLabel } from './ui';
import { DEC_RE, useOrderForm } from './useOrderForm';
import { NumInput } from './NumInput';
import { HelpTip } from './HelpTip';
import { orderHelp } from './orderHelp';
import { EventWarning } from './EventNotice';
import { ctaTone, OrderProblemNote } from './OrderProblemNote';

export function LeverageModal({ open, onClose, value, min = 1, max, onConfirm, available, feeRate, feeFlat }: { open: boolean; onClose: () => void; value: number; min?: number; max: number; onConfirm: (v: number) => void; available: string; feeRate: string; feeFlat: string }) {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value, open]);
  const marks = [min, ...[1, 2, 3].map((k) => Math.round(min + ((max - min) * k) / 4)), max].filter((x, i, a) => a.indexOf(x) === i);
  return (
    <Modal open={open} onClose={onClose} title="Adjust Leverage">
      <p className="m-0 text-[12.5px] text-muted">Cross margin · {min > 1 ? `${min}x–${max}x` : `max ${max}x`} for this instrument</p>
      <div className="num text-center text-[40px] font-semibold">{v}x</div>
      <div>
        <input type="range" min={min} max={max} step={1} value={v} onChange={(e) => setV(Number(e.target.value))} aria-label="Leverage" className="w-full" />
        <div className="num mt-1.5 flex justify-between text-[11px] text-muted">
          {marks.map((m) => (
            <button key={m} type="button" onClick={() => setV(m)} className="hover:text-fg">
              {m}x
            </button>
          ))}
        </div>
      </div>
      <div className="flex flex-col gap-1.5 rounded-lg bg-bg p-3 text-xs">
        <div className="flex justify-between">
          <span className="text-muted">Max position size</span>
          <span className="num">{fmtUsd(maxNotional(available, v, feeRate, feeFlat))}</span>
        </div>
      </div>
      <p className="m-0 text-xs text-muted">Your whole account backs your positions: they are closed only when the account margin level reaches the stop-out level. Higher leverage uses less margin per position.</p>
      <div className="grid grid-cols-2 gap-2.5">
        <button type="button" onClick={onClose} className="h-[42px] rounded-lg border border-line-strong font-semibold text-fg">
          Cancel
        </button>
        <button
          type="button"
          onClick={() => {
            onConfirm(v);
            onClose();
          }}
          className="h-[42px] rounded-lg bg-accent font-semibold text-white"
        >
          Confirm
        </button>
      </div>
    </Modal>
  );
}

function Field({ label, right, bad, children }: { label: React.ReactNode; right?: React.ReactNode; bad?: boolean; children: React.ReactNode }) {
  return (
    <label className={cx('flex flex-col gap-1.5 rounded-lg border bg-bg px-3 py-2', bad ? 'border-warn' : 'border-line focus-within:border-line-strong')}>
      <span className="flex justify-between text-[11px] text-muted">
        <span>{label}</span>
        {right}
      </span>
      {children}
    </label>
  );
}

const inputCls = 'num w-full min-w-0 bg-transparent text-[17px] text-fg outline-none placeholder:text-muted';
const smallInput = 'num h-8 w-full rounded-[7px] border border-line bg-bg px-2.5 text-[12.5px] text-fg outline-none focus:border-line-strong placeholder:text-muted';

/** USD amount box with a `$` prefix, used for TP profit / SL loss. */
function UsdInput({ value, onValue, className }: { value: string; onValue: (v: string) => void; className?: string }) {
  return (
    <span className="relative block">
      <span aria-hidden="true" className="num pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-[12.5px] text-muted">
        $
      </span>
      <NumInput value={value} onValue={onValue} placeholder="0.00" className={cx(smallInput.replace('px-2.5', 'pr-2.5 pl-5'), className)} />
    </span>
  );
}

/** TP / SL price box, with a `$` prefix when the instrument is priced in USD. */
function PriceInput({ usd, bad, value, onValue, placeholder }: { usd: boolean; bad: boolean; value: string; onValue: (v: string) => void; placeholder: string }) {
  const cls = cx(smallInput, bad && 'border-warn focus:border-warn');
  if (!usd) return <NumInput value={value} onValue={onValue} placeholder={placeholder} className={cls} />;
  return (
    <span className="relative block">
      <span aria-hidden="true" className="num pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-[12.5px] text-muted">
        $
      </span>
      <NumInput value={value} onValue={onValue} placeholder={placeholder} className={cx(cls.replace('px-2.5', 'pr-2.5 pl-5'))} />
    </span>
  );
}

/** Price distance from entry, shown under the USD box. `sign` marks the direction of a positive distance. */
function PctNote({ pct, sign, className }: { pct: string; sign: string; className?: string }) {
  const n = Number(pct);
  const txt = pct && Number.isFinite(n) ? `${n >= 0 ? sign : sign === '+' ? '−' : '+'}${Math.abs(n).toFixed(2)}%` : '';
  return <span className={cx('num min-h-[14px] pl-0.5 text-[11px]', className)}>{txt}</span>;
}

export function OrderPanel({ symbol, initialSide }: { symbol: string; initialSide?: Side }) {
  const form = useOrderForm(symbol, initialSide);
  const {
    inst,
    quote,
    me,
    account,
    dp,
    ccy,
    feeRate,
    feeFlat,
    feeLabel,
    available,
    side,
    setSide,
    type,
    setType,
    lev,
    setLev,
    maxLev,
    amount,
    setAmount,
    unit,
    setUnit,
    lots,
    setLots,
    limit,
    setLimit,
    tpsl,
    setTpsl,
    setTp,
    setSl,
    tpVals,
    slVals,
    ref,
    maxN,
    lotsD,
    lotsQty,
    amt,
    pct,
    maxLots,
    calc,
    stepLots,
    problem,
    orderLabel,
    pendType,
    openRef,
    ctaLabel,
    net,
    setPct,
    busy,
    msg,
    submit,
    lotSize,
    lotStep,
    minLots,
    lotDp,
    qdp,
    byLots,
  } = form;
  const [levOpen, setLevOpen] = useState(false);
  const help = orderHelp(form);
  const bad = (f: NonNullable<typeof problem>['field']) => problem?.field === f;
  const usdPriced = ccy === 'USD';
  const freeAfter = calc && me ? D.from(available).sub(calc.margin).sub(calc.fee) : null;
  // Which side of the market makes a limit and which a stop, for the line under the price.
  const lowKind = side === 'long' ? 'Limit' : 'Stop';
  const highKind = side === 'long' ? 'Stop' : 'Limit';
  const kindHint = (k: string) => {
    const name = `${sideLabel(side)} ${k}`;
    return pendType && orderLabel === name ? <b className="font-semibold text-fg">{name}</b> : name;
  };

  return (
    <aside aria-label="Order entry" className="flex h-full flex-col bg-panel">
      <div className="px-4 pt-3.5">
        <div role="group" aria-label="Direction" className="grid grid-cols-2 rounded-lg bg-raised p-[3px]">
          <button type="button" aria-pressed={side === 'long'} onClick={() => setSide('long')} className={cx('h-[34px] rounded-md font-semibold', side === 'long' ? 'bg-btn-long text-btn-long-fg' : 'text-muted')}>
            Buy
          </button>
          <button type="button" aria-pressed={side === 'short'} onClick={() => setSide('short')} className={cx('h-[34px] rounded-md font-semibold', side === 'short' ? 'bg-btn-short text-btn-short-fg' : 'text-muted')}>
            Sell
          </button>
        </div>
      </div>
      <div className="flex items-end gap-4 border-b border-line px-4 pt-3">
        <Tabs
          size="sm"
          value={type}
          onChange={setType}
          tabs={[
            { id: 'market', label: 'Market' },
            { id: 'pending', label: 'Limit / Stop' },
          ]}
        />
        <div className="flex-1" />
        <span className="mb-2 flex items-center gap-1.5">
          <HelpTip help={help.leverage} />
          <button type="button" onClick={() => setLevOpen(true)} className="h-[26px] rounded-md border border-line bg-bg px-2.5 text-xs text-fg">
            Leverage: <span className="num font-semibold">{lev}x</span>
          </button>
        </span>
      </div>

      <form
        className="flex flex-col gap-3.5 px-4 py-3.5"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <div className="flex items-center justify-between gap-2 rounded-lg bg-raised px-2.5 py-1.5 text-xs">
          <span className="flex items-center gap-2 text-muted">
            Order
            <span className={cx('rounded-full px-2 py-0.5 text-[11px] font-bold tracking-wide uppercase', side === 'long' ? 'bg-up-bg text-up' : 'bg-down-bg text-down')}>
              {orderLabel}
            </span>
          </span>
          <HelpTip help={help.type} />
        </div>
        {type === 'pending' && (
          <div className="flex flex-col gap-1">
            <Field
              bad={bad('price')}
              label={
                <span className="flex items-center gap-1">
                  Order price <HelpTip help={help.price} />
                </span>
              }
              right={<button type="button" className="text-accent-fg" onClick={() => quote && setLimit(D.from(side === 'long' ? quote.bid : quote.ask).toFixed(dp))}>{side === 'long' ? 'Bid' : 'Ask'}</button>}
            >
              <span className="flex items-baseline gap-1">
                {usdPriced && <span className="num text-[17px] text-muted">$</span>}
                <NumInput value={limit} onValue={(v) => DEC_RE.test(v) && setLimit(v)} placeholder={quote ? fmt(quote.mark, dp) : '0.00'} aria-label="Order price" className={inputCls} />
              </span>
            </Field>
            {openRef && (
              <span className="num text-[11px] text-muted">
                Below {fmtPrice(openRef, dp, ccy)} → {kindHint(lowKind)} · Above → {kindHint(highKind)}
              </span>
            )}
          </div>
        )}
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted">Order size in</span>
          <div role="group" aria-label="Order size unit" className="grid grid-cols-2 rounded-md bg-raised p-0.5">
            {(['usd', 'lots'] as const).map((u) => (
              <button key={u} type="button" aria-pressed={unit === u} onClick={() => setUnit(u)} className={cx('h-6 rounded-[5px] px-3 text-xs font-semibold', unit === u ? 'bg-bg text-fg shadow-[0_0_0_1px_var(--border-strong)]' : 'text-muted hover:text-fg')}>
                {u === 'usd' ? 'USDT' : 'Lots'}
              </button>
            ))}
          </div>
        </div>

        {byLots ? (
          <>
            <div className={cx('flex flex-col gap-1.5 rounded-lg border bg-bg px-3 py-2', bad('size') ? 'border-warn' : 'border-line focus-within:border-line-strong')}>
              <span className="flex justify-between text-[11px] text-muted">
                <span className="flex items-center gap-1">
                  <label htmlFor="order-lots">Volume</label> <HelpTip help={help.size} />
                </span>
                <span>
                  Max <span className="num text-fg">{fmt(maxLots, lotDp)} lots</span>
                </span>
              </span>
              <span className="flex items-center gap-2">
                <button type="button" onClick={() => stepLots(-1)} aria-label="Decrease volume" className="grid size-8 shrink-0 place-items-center rounded-md border border-line-strong text-base text-fg">
                  −
                </button>
                <NumInput id="order-lots" value={lots} onValue={(v) => DEC_RE.test(v) && setLots(v)} placeholder={D.from(minLots).toFixed(lotDp)} className={cx(inputCls, 'text-center')} />
                <button type="button" onClick={() => stepLots(1)} aria-label="Increase volume" className="grid size-8 shrink-0 place-items-center rounded-md border border-line-strong text-base text-fg">
                  +
                </button>
                <span className="text-xs text-muted">lots</span>
              </span>
            </div>
            <div className="flex gap-1.5">
              {['0.01', '0.1', '0.5', '1'].filter((v) => D.from(v).gte(minLots)).map((v) => (
                <button key={v} type="button" onClick={() => setLots(D.from(v).toFixed(lotDp))} className="num h-[26px] flex-1 rounded-md border border-line text-[11.5px] text-muted hover:border-line-strong hover:text-fg">
                  {v}
                </button>
              ))}
            </div>
            <div className="num grid grid-cols-2 gap-x-3 gap-y-1 rounded-lg border border-dashed border-line-strong px-2.5 py-2 text-[11.5px]">
              <span className="text-muted">Contract size</span>
              <span className="text-right">
                1 lot = {fmt(lotSize, 0)} {unitLabel(inst?.unit ?? '', lotSize)}
              </span>
              <span className="text-muted">Min / step</span>
              <span className="text-right">
                {minLots} / {lotStep} lots
              </span>
              <span className="text-muted">Units</span>
              <span className="text-right">
                {fmt(lotsQty, qdp)} {unitLabel(inst?.unit ?? '', lotsQty)}
              </span>
              <span className="flex items-center gap-1 text-muted">
                Notional <HelpTip help={help.notional} />
              </span>
              <span className="text-right">{fmtUsd(amt)}</span>
            </div>
          </>
        ) : (
          <>
            <Field
              bad={bad('size')}
              label={
                <span className="flex items-center gap-1">
                  Amount <HelpTip help={help.size} />
                </span>
              }
              right={<span>Available <span className="num text-fg">{fmtUsd(available)}</span></span>}>
              <span className="flex items-baseline gap-1">
                <span className="num text-[17px] text-muted">$</span>
                <NumInput value={amount} onValue={(v) => DEC_RE.test(v) && setAmount(v)} placeholder="0.00" aria-label="Amount in USDT" className={inputCls} />
              </span>
            </Field>
            <div className="flex justify-between text-[11.5px] text-muted">
              <span className="num">
                ≈ {calc ? fmt(calc.qty, qdp, 'down') : '0'} {unitLabel(inst?.unit ?? symbol, calc?.qty ?? 0)}
              </span>
              <span className="num">= {calc ? fmtLots(calc.qty, lotSize) : '0.00'} lots</span>
            </div>
            <div>
              <input type="range" min={0} max={100} step={1} value={Math.round(pct)} onChange={(e) => setPct(Number(e.target.value))} aria-label="Percent of max size" className="m-0 w-full" disabled={!maxN.isPos()} />
              <div className="mt-1 flex justify-between">
                {[0, 25, 50, 75, 100].map((p) => (
                  <button key={p} type="button" onClick={() => setPct(p)} className={cx('num text-[11px]', Math.round(pct) === p ? 'text-fg' : 'text-muted hover:text-fg')}>
                    {p}%
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        <div className="flex items-center gap-1.5">
          <button type="button" role="switch" aria-checked={tpsl} onClick={() => setTpsl(!tpsl)} className="flex items-center gap-2 rounded-md text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent">
            <span aria-hidden="true" className={cx('relative h-5 w-[34px] shrink-0 rounded-full transition-colors', tpsl ? 'bg-up' : 'bg-line-strong')}>
              <span className={cx('absolute top-0.5 left-0.5 size-4 rounded-full bg-white shadow transition-transform', tpsl && 'translate-x-3.5')} />
            </span>
            <span>TP / SL</span>
          </button>
          <span className="flex items-center gap-1 text-xs text-muted">
            <HelpTip help={help.tpsl} />
          </span>
        </div>

        {tpsl && (
          <div className="grid grid-cols-[1fr_112px] gap-x-2 gap-y-1">
            <label className="flex flex-col gap-1 text-[11px] text-muted">
              TP Price
              <PriceInput usd={usdPriced} bad={bad('tp')} value={tpVals.price} onValue={(v) => DEC_RE.test(v) && setTp(v ? { kind: 'price', value: v } : null)} placeholder="Enter TP price" />
            </label>
            <label className="flex flex-col gap-1 text-[11px] text-muted">
              Profit (USDT)
              <UsdInput value={tpVals.usd} onValue={(v) => DEC_RE.test(v) && setTp(v ? { kind: 'usd', value: v } : null)} className="text-up" />
            </label>
            <span />
            <PctNote pct={tpVals.pct} sign="+" className="text-up" />
            <label className="flex flex-col gap-1 text-[11px] text-muted">
              SL Price
              <PriceInput usd={usdPriced} bad={bad('sl')} value={slVals.price} onValue={(v) => DEC_RE.test(v) && setSl(v ? { kind: 'price', value: v } : null)} placeholder="Enter SL price" />
            </label>
            <label className="flex flex-col gap-1 text-[11px] text-muted">
              Loss (USDT)
              <UsdInput value={slVals.usd} onValue={(v) => DEC_RE.test(v) && setSl(v ? { kind: 'usd', value: v } : null)} className="text-down" />
            </label>
            <span />
            <PctNote pct={slVals.pct} sign="−" className="text-down" />
            <p className="col-span-2 m-0 mt-1 text-[11px] text-muted">Enter a price or a USDT amount. % is the distance from your entry price.</p>
          </div>
        )}

        <EventWarning symbol={symbol} />
        <button type="submit" disabled={busy || !!problem} className={cx('h-11 truncate rounded-lg px-2 text-sm font-bold disabled:cursor-not-allowed', ctaTone(side, !!problem))}>
          {busy ? <Spinner /> : ctaLabel}
        </button>
        {problem && <OrderProblemNote problem={problem} />}
        {msg && (
          <p className={cx('m-0 text-xs', msg.ok ? 'text-up' : 'text-down')} role="status">
            {msg.text}
          </p>
        )}

        <div className="flex flex-col gap-1.5 text-xs">
          <div className="flex justify-between">
            <span className="text-muted">{type === 'pending' ? (pendType ? `${orderLabel} price` : 'Order price') : `Est. entry (${side === 'long' ? 'ask' : 'bid'})`}</span>
            <span className="num">{ref ? fmtPrice(ref, dp, ccy) : '—'}</span>
          </div>
          <div className="flex justify-between">
            <span className="flex items-center gap-1 text-muted">
              Margin required{calc?.hedges ? ' (hedged)' : ''} <HelpTip help={help.margin} />
            </span>
            <span className="num">{fmtUsd(calc ? calc.margin : 0)}</span>
          </div>
          <div className="flex justify-between">
            <span className="flex items-center gap-1 text-muted">
              Fee{feeLabel && ` (${feeLabel})`} <HelpTip help={help.fee} />
            </span>
            <span className="num">{fmtUsd(calc ? calc.fee : 0)}</span>
          </div>
          <div className="flex justify-between">
            <span className="flex items-center gap-1 text-muted">
              Free margin after <HelpTip help={help.free} />
            </span>
            <span className={cx('num', freeAfter?.isNeg() && 'text-warn')}>{freeAfter ? fmtUsd(freeAfter) : '—'}</span>
          </div>
          <div className="flex justify-between">
            <span className="flex items-center gap-1 text-muted">
              Value of $1 move <HelpTip help={help.pointValue} />
            </span>
            <span className="num">{fmtUsd(calc ? calc.pointValue : 0)}</span>
          </div>
        </div>
      </form>

      <div className="mt-auto flex flex-col gap-2.5 border-t border-line p-4 text-[12.5px]">
        <div className="flex items-center justify-between">
          <span className="text-muted">Available Margin</span>
          <span className="num rounded-[5px] bg-accent-bg px-2 py-0.5 text-accent-fg">{fmtUsd(available)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted">Account Value</span>
          <span className="num">{fmtUsd(account?.equity ?? '0')}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted">Margin Used</span>
          <span className="num">{fmtUsd(account?.marginUsed ?? '0')}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted">Current Position</span>
          <span className={cx('num', net.isPos() ? 'text-up' : net.isNeg() ? 'text-down' : 'text-muted')}>
            {net.isZero() ? '—' : `${net.isPos() ? '+' : ''}${fmt(qtyToLots(net, lotSize), 2)} lots`}
          </span>
        </div>
        {account && (
          <span className="text-[11px] text-muted">
            {account.kind === 'demo' ? 'Demo account · virtual funds' : 'Live account'}
          </span>
        )}
      </div>

      {inst && (
        <LeverageModal open={levOpen} onClose={() => setLevOpen(false)} value={lev} min={inst.minLeverage} max={maxLev} onConfirm={setLev} available={available} feeRate={feeRate} feeFlat={feeFlat} />
      )}
    </aside>
  );
}
